/** @format */

import { apiClient } from "@/api/axiosClient.ts";
import { handleApiError } from "./common.ts";
import {
  getAccessToken,
  getRefreshToken,
  setTokens as baseSetTokens,
  clearTokens,
  isTokenExpired as baseIsTokenExpired,
} from "../authUtils.ts";

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access: string;
  refresh: string;
}

export interface RefreshTokenRequest {
  refresh: string;
}

export interface RefreshTokenResponse {
  access: string;
  refresh: string;
}

// Configurations types
export interface Group {
  id: number;
  name: string;
}

export interface ConfigurationsItem {
  groups: Group[];
  [key: string]: any;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

// Some backends may return a list with a single item; keep it flexible
export type ConfigurationsResponse = ConfigurationsItem | ConfigurationsItem[];

// Utility functions for token management
export const tokenUtils = {
  getAccessToken,
  getRefreshToken,
  setTokens: (accessToken: string, refreshToken: string): void => {
    baseSetTokens(accessToken, refreshToken, true);
  },
  clearTokens,
  isTokenExpired: baseIsTokenExpired,
};

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>(
        "/auth/token/",
        credentials
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Login failed");
    }
  },

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const response = await apiClient.post<RefreshTokenResponse>(
        "/auth/token/refresh/",
        { refresh: refreshToken }
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Token refresh failed");
    }
  },

  async getConfigurations(): Promise<ConfigurationsResponse> {
    try {
      const response = await apiClient.get<ConfigurationsResponse>(
        "/auth/configurations/"
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch configurations");
    }
  },

  /**
   * Change the current user's password
   * PUT /auth/user/change-password/
   */
  async changePassword(payload: ChangePasswordRequest): Promise<void> {
    try {
      await apiClient.put("/auth/user/change-password/", payload);
    } catch (error) {
      handleApiError(error, "Failed to change password");
    }
  },
};
